const PUBLIC_ROUTES = new Set([
  '/signin',
  '/signup',
  '/protected',
  '/expired',
  '/auth',
]);

function normalizePathname(pathname) {
  const path = String(pathname || window.location?.pathname || '/')
    .split('?')[0]
    .replace(/\/+$/, '');
  return path || '/';
}

function isPublicRoute(pathname) {
  return PUBLIC_ROUTES.has(normalizePathname(pathname));
}

const crispState = {
  initialized: false,
  currentTokenId: null,
  currentNickname: null,
  currentEmailKey: null,
  currentDataKey: null,
  routeHidden: false,
  bottomOffsetObserverAttached: false,
  /** Après chargement document : un seul `chat:close` pour éviter l’ouverture / l’aperçu restauré par Crisp, sans refermer à chaque navigation SPA. */
  documentBootCloseDone: false,
};

function isCrispEnabled() {
  return (
    process.env.VUE_APP_CRISP_ENABLED === 'true' &&
    !!process.env.VUE_APP_CRISP_WEBSITE_ID
  );
}

function getCrispPosition() {
  const raw = String(process.env.VUE_APP_CRISP_POSITION || 'right')
    .trim()
    .toLowerCase();
  return raw === 'left' ? 'left' : 'right';
}

function getCrispBottomOffsetPx() {
  const value = Number(process.env.VUE_APP_CRISP_BOTTOM_OFFSET_PX || 8);
  if (!Number.isFinite(value)) return 8;
  return Math.max(0, Math.min(Math.round(value), 160));
}

function getQueue() {
  window.$crisp = window.$crisp || [];
  return window.$crisp;
}

function enqueue(command) {
  getQueue().push(command);
}

function injectCrispScript() {
  if (!document || document.getElementById('crisp-chat-sdk')) return;
  const script = document.createElement('script');
  script.id = 'crisp-chat-sdk';
  script.src = 'https://client.crisp.chat/l.js';
  script.async = true;
  document.head.appendChild(script);
}

function applyCrispBottomOffset() {
  if (typeof document === 'undefined') return;
  const configuredOffset = getCrispBottomOffsetPx();
  const offset = `${configuredOffset}px`;
  const root = document.getElementById('crisp-chatbox');
  if (root) {
    root.style.setProperty('bottom', offset, 'important');
    root.style.setProperty('inset', `auto 0 ${offset} auto`, 'important');
    root.style.setProperty('transform', 'none', 'important');
  }

  // Some Crisp layouts position the iframe directly.
  document.querySelectorAll('#crisp-chatbox iframe').forEach((iframe) => {
    iframe.style.setProperty('bottom', offset, 'important');
    iframe.style.setProperty('inset', `auto 0 ${offset} auto`, 'important');
  });

  // Extra safety: Crisp can overwrite inline styles after async boot.
  let styleTag = document.getElementById('crisp-bottom-offset-style');
  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = 'crisp-bottom-offset-style';
    document.head.appendChild(styleTag);
  }
  styleTag.textContent = `
#crisp-chatbox { bottom: ${offset} !important; }
#crisp-chatbox > div { bottom: ${offset} !important; }
#crisp-chatbox iframe { bottom: ${offset} !important; }
#crisp-chatbox [style*="bottom"] { bottom: ${offset} !important; }
`;
}

function attachBottomOffsetObserver() {
  if (typeof document === 'undefined' || crispState.bottomOffsetObserverAttached)
    return;

  crispState.bottomOffsetObserverAttached = true;
  applyCrispBottomOffset();

  const observer = new MutationObserver(() => {
    applyCrispBottomOffset();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Retry for early load frames where crisp root appears asynchronously.
  let tries = 0;
  const retryTimer = setInterval(() => {
    applyCrispBottomOffset();
    tries += 1;
    if (tries >= 50) clearInterval(retryTimer);
  }, 400);
}

function init({ tokenId } = {}) {
  if (!isCrispEnabled() || crispState.initialized || typeof window === 'undefined')
    return false;

  window.CRISP_WEBSITE_ID = process.env.VUE_APP_CRISP_WEBSITE_ID;

  // CRITICAL: set continuity token BEFORE loading Crisp script when available.
  // Never use DID/email — only the HMAC token issued by the backend.
  if (tokenId) {
    window.CRISP_TOKEN_ID = tokenId;
    crispState.currentTokenId = tokenId;
  }

  const previousReadyTrigger = window.CRISP_READY_TRIGGER;
  window.CRISP_READY_TRIGGER = () => {
    if (typeof previousReadyTrigger === 'function') {
      previousReadyTrigger();
    }
    applyCrispBottomOffset();
    enqueue(['do', 'chat:close']);
    // Crisp peut rouvrir le panneau ou l’aperçu après restauration de session ; refermer une fois le runtime prêt.
    requestAnimationFrame(() => {
      enqueue(['do', 'chat:close']);
      setTimeout(() => enqueue(['do', 'chat:close']), 120);
    });
  };
  getQueue();
  enqueue(['safe', true]);
  enqueue(['config', 'position:reverse', [getCrispPosition() === 'left']]);
  // Hide first to prevent a flash-open during startup restore.
  enqueue(['do', 'chat:hide']);
  enqueue(['do', 'chat:close']);
  injectCrispScript();
  attachBottomOffsetObserver();

  crispState.initialized = true;
  return true;
}

/**
 * Secure session continuity token from backend HMAC only.
 * NEVER use DID, email, or any public/guessable value — Crisp docs warn this
 * lets anyone restore another user's support conversation.
 */
function normalizeTokenId(session) {
  if (!session || session.isTmp) return null;
  const tokenId = session?.crisp?.tokenId;
  if (!tokenId || typeof tokenId !== 'string') return null;
  const trimmed = tokenId.trim();
  // Reject obviously insecure / legacy patterns if they ever leak through.
  if (!trimmed || trimmed.startsWith('studio-') || trimmed.startsWith('did:')) {
    return null;
  }
  return trimmed;
}

function setTokenId(tokenId) {
  const normalizedToken = tokenId || null;
  if (crispState.currentTokenId === normalizedToken) return;

  const previous = crispState.currentTokenId;
  window.CRISP_TOKEN_ID = normalizedToken || '';

  const isFirstBind = previous === null && normalizedToken !== null;
  const isLogout = previous !== null && normalizedToken === null;
  const isSwitchUser =
    previous !== null &&
    normalizedToken !== null &&
    previous !== normalizedToken;

  // Reset when leaving a bound session or switching accounts.
  // On first bind after Crisp already loaded without a token, reset so the
  // continuity token is applied (Crisp docs: set token then session:reset).
  if (isLogout || isSwitchUser || (isFirstBind && crispState.initialized)) {
    enqueue(['do', 'session:reset']);
  }
  if (!isFirstBind && (isLogout || isSwitchUser)) {
    enqueue(['do', 'chat:close']);
  }

  crispState.currentTokenId = normalizedToken;
  crispState.currentNickname = null;
  crispState.currentEmailKey = null;
  crispState.currentDataKey = null;
}

function setNickname(nickname) {
  if (!nickname || crispState.currentNickname === nickname) return;
  enqueue(['set', 'user:nickname', [nickname]]);
  crispState.currentNickname = nickname;
}

function setEmail(email, signature) {
  if (!email) return;
  const key = `${email}:${signature || ''}`;
  if (crispState.currentEmailKey === key) return;

  // Prefer signed identity when available (Identity Verification).
  // Unsigned email still identifies the user in the inbox, but is not "verified".
  if (signature) {
    enqueue(['set', 'user:email', [email, signature]]);
  } else {
    enqueue(['set', 'user:email', [email]]);
  }
  crispState.currentEmailKey = key;
}

function setSessionData(data = {}) {
  const entries = Object.entries(data).filter(([, value]) =>
    ['string', 'number', 'boolean'].includes(typeof value)
  );
  if (!entries.length) return;

  const key = JSON.stringify(entries);
  if (crispState.currentDataKey === key) return;
  enqueue(['set', 'session:data', [entries]]);
  crispState.currentDataKey = key;
}

function setSegments(data = {}) {
  const segments = [];
  if (data.authType) segments.push(`auth:${data.authType}`);
  if (data.isContributor === true) segments.push('role:contributor');
  if (data.isSubCommunity === true) segments.push('scope:sub-community');
  if (!segments.length) return;
  enqueue(['set', 'session:segments', [segments, true]]);
}

function setVisibility(pathname) {
  const path = normalizePathname(pathname);
  if (isPublicRoute(path)) {
    enqueue(['do', 'chat:hide']);
    crispState.routeHidden = true;
  } else {
    enqueue(['do', 'chat:show']);
    // Fermer le panneau quand on quitte une route publique (login, etc.).
    if (crispState.routeHidden) {
      enqueue(['do', 'chat:close']);
      crispState.routeHidden = false;
      crispState.documentBootCloseDone = true;
    } else if (!crispState.documentBootCloseDone) {
      // Premier affichage sur une route privée : la file contient déjà chat:show après
      // chat:close (init), ce qui laissait le widget rouvrir l’aperçu — on referme une fois.
      enqueue(['do', 'chat:close']);
      crispState.documentBootCloseDone = true;
    }
  }
}

function buildDisplayNickname(session, issuerInfo) {
  if (issuerInfo?.name) return issuerInfo.name;
  if (session?.crisp?.nickname) return session.crisp.nickname;
  if (session?.connexionEmail) return session.connexionEmail.split('@')[0];
  if (!session?.did) return null;
  return session.did.length > 20
    ? `${session.did.slice(0, 12)}...${session.did.slice(-6)}`
    : session.did;
}

/**
 * Unbind Crisp from the current browser session (logout / account switch).
 * Does not delete the remote conversation — only clears local binding.
 */
function clearSession() {
  if (typeof window === 'undefined') return;
  window.CRISP_TOKEN_ID = '';
  crispState.currentTokenId = null;
  crispState.currentNickname = null;
  crispState.currentEmailKey = null;
  crispState.currentDataKey = null;
  if (crispState.initialized) {
    enqueue(['do', 'session:reset']);
    enqueue(['do', 'chat:close']);
  }
}

function sync({ session, path, issuerInfo } = {}) {
  if (!isCrispEnabled() || typeof window === 'undefined') return;
  const pathname = normalizePathname(path);
  if (isPublicRoute(pathname)) {
    if (crispState.initialized) setVisibility(pathname);
    return;
  }

  const tokenId =
    session && !session.isTmp ? normalizeTokenId(session) : null;

  init({ tokenId });
  setVisibility(pathname);

  if (!session || session?.isTmp) {
    setTokenId(null);
    return;
  }

  setTokenId(tokenId);

  // Without a secure continuity token, do not bind identity to a lasting session.
  if (!tokenId) return;

  const nickname = buildDisplayNickname(session, issuerInfo);
  setNickname(nickname);
  setEmail(
    session?.crisp?.email || session?.connexionEmail,
    session?.crisp?.emailSignature
  );
  setSessionData({
    ...(session?.crisp?.data || {}),
    did: session?.did,
  });
  setSegments(session?.crisp?.data || {});
}

export default {
  init,
  sync,
  clearSession,
};
